import { useState, useCallback, useEffect, useRef } from 'react'
import { get } from '../../api/client.ts'

interface SearchResult {
  type: string
  id: number
  label: string
  sublabel: string | null
  // Point for meters/transformers/substations; LineString for feeder routes
  location: { type: string; coordinates: number[] | number[][] } | null
}

interface SearchBarProps {
  onResultSelect: (result: SearchResult) => void
}

export function SearchBar({ onResultSelect }: SearchBarProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  /** Set when a completed search returned zero rows — drives the explicit
   *  "No matches" dropdown state (C7) instead of silently closing. */
  const [noMatchQuery, setNoMatchQuery] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([])
      setIsOpen(false)
      setNoMatchQuery(null)
      return
    }

    try {
      const data = await get<SearchResult[]>(`/search?q=${encodeURIComponent(q)}&limit=20`)
      setResults(data)
      // C7: an empty result set OPENS the dropdown with an explicit empty
      // state — silently closing reads as "search is broken".
      setNoMatchQuery(data.length === 0 ? q : null)
      setIsOpen(true)
      setSelectedIndex(-1)
    } catch {
      // Request errors still close quietly; friendly error surfacing is the
      // Stage-2 A8 sweep.
      setResults([])
      setNoMatchQuery(null)
      setIsOpen(false)
    }
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => doSearch(query), 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query, doSearch])

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault()
      onResultSelect(results[selectedIndex])
      setIsOpen(false)
      setQuery('')
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const typeColors: Record<string, string> = {
    meter: 'bg-gray-200 text-gray-700',
    transformer: 'bg-yellow-200 text-yellow-800',
    feeder: 'bg-blue-200 text-blue-800',
    substation: 'bg-red-200 text-red-800',
    device: 'bg-purple-200 text-purple-800',
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => (results.length > 0 || noMatchQuery !== null) && setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        placeholder="Search meters, transformers, devices..."
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-y-auto z-50">
          {results.length === 0 && noMatchQuery !== null && (
            <div className="px-3 py-2 text-sm text-gray-500">
              No matches for '{noMatchQuery}' — search covers meters,
              transformers, feeders, substations, and protective devices.
            </div>
          )}
          {results.map((result, index) => (
            <button
              key={`${result.type}-${result.id}`}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 ${
                index === selectedIndex ? 'bg-blue-50' : ''
              }`}
              onMouseDown={() => {
                onResultSelect(result)
                setIsOpen(false)
                setQuery('')
              }}
            >
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${typeColors[result.type] || 'bg-gray-100'}`}>
                {result.type}
              </span>
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium text-gray-800">{result.label}</div>
                {result.sublabel && (
                  <div className="truncate text-xs text-gray-500">{result.sublabel}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
